import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Building, Check } from 'lucide-react';
import type { CityToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useCitiesList, useCityRegions, useCityMutations } from '~/hooks/use-cities';
import { tokenToForm, type CityFormValues } from '~/lib/schemas/city';
import { CityEditDialog } from './city-edit-dialog';

/** Cities CRUD surface — parity with the legacy admin grid (Region/Name/Code/Visible/Sort). */
export function CitiesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useCitiesList();
	const { data: regions = [] } = useCityRegions();
	const { remove } = useCityMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<CityFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<CityToken | null>(null);

	const regionMap = useMemo(() => new Map(regions.map(r => [r.id, r.nm])), [regions]);
	const regionOf = (c: CityToken) => regionMap.get(c.crid) ?? '';

	const columns = useMemo<SystemGridColumn<CityToken>[]>(
		() => [
			{
				key: 'region',
				header: t('system.cities.columns.region'),
				width: '200px',
				sortValue: c => regionOf(c),
				exportValue: c => regionOf(c),
				render: c => <span className="truncate text-sm text-foreground-muted">{regionOf(c) || '—'}</span>
			},
			{
				key: 'name',
				header: t('system.cities.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: c => c.nm,
				exportValue: c => c.nm,
				render: c => <span className="truncate text-sm font-medium text-foreground">{c.nm}</span>
			},
			{
				key: 'code',
				header: t('system.cities.columns.code'),
				width: '120px',
				sortValue: c => c.cd ?? '',
				exportValue: c => c.cd ?? '',
				render: c => <span className="font-mono text-xs text-foreground-muted">{c.cd || '—'}</span>
			},
			{
				key: 'erpCode',
				header: t('system.fields.erpCode'),
				width: '120px',
				defaultVisible: false,
				sortValue: c => c.erpcd ?? '',
				exportValue: c => c.erpcd ?? '',
				render: c => <span className="font-mono text-xs text-foreground-muted">{c.erpcd || '—'}</span>
			},
			{
				key: 'visible',
				header: t('system.cities.columns.visible'),
				width: '80px',
				align: 'center',
				sortValue: c => (c.iv ? 1 : 0),
				exportValue: c => (c.iv ? t('common.yes') : t('common.no')),
				render: c => (c.iv ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>)
			},
			{
				key: 'sort',
				header: t('system.cities.columns.sort'),
				width: '70px',
				align: 'right',
				sortValue: c => c.si ?? 0,
				exportValue: c => c.si ?? 0,
				render: c => <span className="text-sm tabular-nums text-foreground-muted">{c.si}</span>
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t, regionMap]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<CityToken>
				gridId="cities"
				title={t('system.cities.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={c => c.id ?? c.nm}
				columns={columns}
				searchPredicate={(c, q) => `${c.nm ?? ''} ${c.cd ?? ''} ${regionOf(c)}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'name', dir: 'asc' }}
				emptyIcon={<Building size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('system.cities.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={c => {
					setEditing(tokenToForm(c));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="cities"
			/>

			<CityEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} regions={regions} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.cities.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('system.cities.deleteBody', { name: deleteRow?.nm ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
